/**
 * AdminPersonelYonetimi.jsx
 *
 * Personel Yonetimi — Panel sadelestirme Tur 2 (2026-09-29). Eskiden ayri
 * duran `/admin/users` (Kullanici Yonetimi) sayfasinin rol degistirme
 * yetenegini "Ekip Yonetimi" grubuna tasir (bkz. proje-yoneticisi gorev
 * talimati). Kullanicilar (AdminUsers.js) sayfasi SILINDI — bu sayfa onun
 * yerine gecmez, yalniz ROL DEGISTIRME + YENI PERSONEL kisayolunu tasir.
 *
 * Route: /admin/personel-yonetimi
 * Erisim: admin, superadmin.
 *
 * Backend: GET /admin/users (arama + rol filtresi + sayfalama, degismedi)
 *          PUT /admin/users/{user_id}/role  body {"role": "..."}
 *          (cagri AdminUsers.js:79'dan BIREBIR tasindi)
 *
 * KAPSAM DISI (bilerek): ban/unban, XP gosterimi — eski AdminUsers.js'te
 * vardi ama gorev talimati yalniz rol degistirme + yeni personel butonunu
 * istedi. Bu ozellikler hicbir yerde yeniden yapilmadi (proje-yoneticisine
 * bulgu olarak raporlandi).
 */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from '../components/admin/AdminLayout';
import PageHeader from '../components/admin/PageHeader';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { getApiErrorMessage } from '../utils/apiError';
import { rootBreadcrumb } from '../utils/adminBreadcrumbs';
import { Skeleton } from '../components/ui/skeleton';
import { Search, ChevronLeft, ChevronRight, UserPlus, UserCog } from 'lucide-react';
import { motion } from 'framer-motion';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_FILTERS = ['', 'student', 'instructor', 'danisman', 'admin', 'superadmin'];

export default function AdminPersonelYonetimi() {
  const { lang } = useLanguage();
  const { authHeaders, user } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const tr = (t, e) => (lang === 'tr' ? t : e);
  // Reviewer fix turu (2026-09-29): backend allowlist'i (PUT /admin/users/{id}/role)
  // yalniz student/instructor/admin/danisman kabul ediyor — superadmin hicbir
  // zaman hedef olamaz. Ayrica admin rolune atama artik yalniz superadmin
  // gorunumunde acik; duz admin gorunumunde "Admin" secenegi disabled kalir
  // (satirin MEVCUT rolu zaten admin ise disable etmiyoruz — aksi halde o
  // satirin secili degerini <select> eslestiremez, bos/karisik gorunur).
  const isSuperadmin = user?.role === 'superadmin';

  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const limit = 20;

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (roleFilter) params.set('role', roleFilter);
      params.set('skip', page * limit);
      params.set('limit', limit);
      const res = await axios.get(`${API}/admin/users?${params}`, { headers: authHeaders() });
      setUsers(res.data.users);
      setTotal(res.data.total);
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, roleFilter, page]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  // AdminUsers.js:79'dan birebir tasindi — davranis degismedi.
  const changeRole = async (userId, newRole) => {
    const u = users.find(x => x.user_id === userId);
    const userName = u?.name || u?.email || tr('kullanici', 'user');
    const oldRole = u?.role;

    if (oldRole === newRole) return;

    const isPromotingToAdmin = newRole === 'admin' || newRole === 'superadmin';
    const isDemotingFromAdmin = oldRole === 'admin' || oldRole === 'superadmin';

    let confirmMsg;
    if (isPromotingToAdmin) {
      confirmMsg = tr(
        `${userName} kullanicisini ${newRole} yapmak istediginize emin misiniz?\n\nBu kullanici tam sistem erisimine sahip olacak.`,
        `Are you sure you want to make ${userName} a ${newRole}?\n\nThis grants full system access.`
      );
    } else if (isDemotingFromAdmin) {
      confirmMsg = tr(
        `${userName} kullanicisini admin rolunden cikarmak istediginize emin misiniz?\n\nBu kullanici admin paneline erisemeyecek.`,
        `Demote ${userName} from admin role?\n\nThis user will lose admin panel access.`
      );
    } else {
      confirmMsg = tr(
        `${userName} kullanicisinin rolunu ${newRole} olarak degistirmek istediginize emin misiniz?`,
        `Change ${userName}'s role to ${newRole}?`
      );
    }

    if (!window.confirm(confirmMsg)) {
      fetchUsers();
      return;
    }

    try {
      await axios.put(`${API}/admin/users/${userId}/role`, { role: newRole }, { headers: authHeaders() });
      toast.success(tr(`Rol "${newRole}" olarak güncellendi`, `Role updated to "${newRole}"`));
      fetchUsers();
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang));
      fetchUsers();
    }
  };

  return (
    <AdminLayout>
      <div data-testid="admin-personel-yonetimi-content">
        <PageHeader
          breadcrumbs={[
            rootBreadcrumb(user?.role, tr),
            { label: tr('Ekip Yönetimi', 'Team Management') },
            { label: tr('Personel Yönetimi', 'Personnel Management') },
          ]}
          title={tr('Personel Yönetimi', 'Personnel Management')}
          subtitle={tr('Personel listesi ve rol değiştirme — öğrenci kaydı için Öğrenciler sayfasını kullanın.', 'Personnel list and role changes — use the Students page for student registration.')}
          actions={
            <motion.button
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              onClick={() => navigate('/admin/users/new')}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-cyan-500 text-white text-sm font-bold border-2 border-cyan-600 hover:bg-cyan-600 transition-colors"
              data-testid="new-personnel-btn"
            >
              <UserPlus size={16} />
              {tr('Yeni Personel', 'New Personnel')}
            </motion.button>
          }
        />

        {/* Filters */}
        <div className="flex flex-wrap gap-3 mb-6">
          <div className="flex-1 min-w-[200px] relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" size={18} />
            <input
              type="text" value={search} onChange={e => { setSearch(e.target.value); setPage(0); }}
              placeholder={tr('Kullanici ara...', 'Search users...')}
              className="w-full pl-10 pr-4 py-3 rounded-xl border-2 border-[var(--border-light)] bg-white focus:border-[var(--brand-primary)] focus:outline-none font-semibold"
              data-testid="personel-search"
            />
          </div>
          {ROLE_FILTERS.map(r => (
            <button key={r} onClick={() => { setRoleFilter(r); setPage(0); }}
              className={`px-4 py-2 rounded-xl font-bold text-sm border-2 transition-all ${
                roleFilter === r
                  ? r === 'superadmin'
                    ? 'bg-rose-600 text-white border-rose-700'
                    : 'bg-[var(--brand-primary)] text-white border-[var(--brand-primary-shadow)]'
                  : 'bg-white border-[var(--border-light)]'
              }`}
              data-testid={`filter-role-${r || 'all'}`}
            >
              {r ? r.charAt(0).toUpperCase() + r.slice(1) : tr('Tümü', 'All')}
            </button>
          ))}
        </div>

        {loading && (
          <div className="space-y-2">
            {[1, 2, 3, 4, 5].map(i => <Skeleton key={i} className="h-14 w-full" />)}
          </div>
        )}

        {!loading && users.length === 0 && (
          <div className="card-clay p-8 text-center">
            <UserCog size={32} className="mx-auto mb-2 text-[var(--text-muted)]" />
            <p className="text-sm font-semibold text-[var(--text-muted)]">
              {tr('Aramanızla eşleşen kullanıcı yok.', 'No users match your search.')}
            </p>
          </div>
        )}

        {!loading && users.length > 0 && (
          <div className="card-clay overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left p-4 font-bold">{tr('Kullanici', 'User')}</th>
                    <th className="text-left p-4 font-bold">Email</th>
                    <th className="text-left p-4 font-bold">{tr('Rol', 'Role')}</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u, i) => (
                    <motion.tr key={u.user_id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: i * 0.03 }}
                      className="border-t border-[var(--border-light)] hover:bg-gray-50"
                      data-testid={`personel-row-${i}`}
                    >
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-[var(--brand-primary)] flex items-center justify-center overflow-hidden">
                            {u.picture ? <img src={u.picture} alt="" className="w-full h-full object-cover" /> : <span className="text-white text-xs font-bold">{u.name?.[0]}</span>}
                          </div>
                          <span className="font-bold">{u.name}</span>
                        </div>
                      </td>
                      <td className="p-4 text-[var(--text-muted)]">{u.email}</td>
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          <select value={u.role} onChange={e => changeRole(u.user_id, e.target.value)}
                            className={`px-2 py-1 rounded-lg border-2 text-xs font-bold bg-white focus:outline-none ${
                              u.role === 'superadmin'
                                ? 'border-rose-400 text-rose-700'
                                : 'border-[var(--border-light)]'
                            }`}
                            data-testid={`role-select-${i}`}
                          >
                            <option value="student">Student</option>
                            <option value="instructor">Instructor</option>
                            <option value="danisman">Danisman</option>
                            <option value="admin" disabled={!isSuperadmin && u.role !== 'admin'}>Admin</option>
                            {u.role === 'superadmin' && (
                              <option value="superadmin" disabled>Superadmin</option>
                            )}
                          </select>
                          {u.role === 'superadmin' && (
                            <span className="px-1.5 py-0.5 bg-gradient-to-r from-rose-500 to-orange-500 text-white text-[9px] font-bold rounded-md whitespace-nowrap">
                              SUPER
                            </span>
                          )}
                        </div>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* Pagination */}
            <div className="flex items-center justify-between p-4 border-t border-[var(--border-light)]">
              <span className="text-sm text-[var(--text-muted)] font-semibold">{total} {tr('kullanıcı', 'users')}</span>
              <div className="flex gap-2">
                <button onClick={() => setPage(Math.max(0, page - 1))} disabled={page === 0}
                  className="p-2 rounded-lg border-2 border-[var(--border-light)] disabled:opacity-40" data-testid="prev-page-btn">
                  <ChevronLeft size={16} />
                </button>
                <span className="px-3 py-2 text-sm font-bold">{page + 1}</span>
                <button onClick={() => setPage(page + 1)} disabled={(page + 1) * limit >= total}
                  className="p-2 rounded-lg border-2 border-[var(--border-light)] disabled:opacity-40" data-testid="next-page-btn">
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
